import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base-http.service';
import {
  ProductDetail,
  ProductPlan,
  ProductPlanDetail,
  ProductFeature,
  ProductBenefit,
  ProductCoverage,
  PaymentTerm,
  ProductInterestedRequest,
  PlanThumbnail,
  ProductRider
} from '../models';

/**
 * Product Service for IH API
 * Handles product information and details
 * Always calls real backend API
 */
@Injectable({
  providedIn: 'root'
})
export class ProductService extends BaseHttpService {
  private readonly PRODUCT_ENDPOINT = 'product';

  /**
   * Get all active products
   * GET /api/ih/product/list
   */
  getProductList(): Observable<ProductDetail[]> {
    return this.get<ProductDetail[]>(`${this.PRODUCT_ENDPOINT}/list`);
  }

  /**
   * Get product details
   * GET /api/ih/product/{productId}
   */
  getProductDetails(productId: string): Observable<ProductDetail> {
    return this.get<ProductDetail>(`${this.PRODUCT_ENDPOINT}/${productId}`);
  }

  /**
   * Get product plans
   * GET /api/ih/product/{productId}/plans
   */
  getProductPlans(productId: string): Observable<ProductPlan[]> {
    return this.get<ProductPlan[]>(`${this.PRODUCT_ENDPOINT}/${productId}/plans`);
  }

  /**
   * Get plan details with options, coverages, and payment terms
   * GET /api/ih/product/plan/{planId}
   */
  getPlanDetails(planId: string): Observable<ProductPlanDetail> {
    return this.get<ProductPlanDetail>(`${this.PRODUCT_ENDPOINT}/plan/${planId}`);
  }

  /**
   * Submit product interest form
   * POST /api/ih/product/interested
   */
  submitProductInterest(request: ProductInterestedRequest): Observable<any> {
    return this.post<any>(`${this.PRODUCT_ENDPOINT}/interested`, request);
  }

  /**
   * Get product features
   * GET /api/ih/product/features/{productId}
   */
  getProductFeatures(productId: string): Observable<ProductFeature[]> {
    return this.get<ProductFeature[]>(`${this.PRODUCT_ENDPOINT}/features/${productId}`);
  }

  /**
   * Get product benefits
   * GET /api/ih/product/benefits/{productId}
   */
  getProductBenefits(productId: string): Observable<ProductBenefit[]> {
    return this.get<ProductBenefit[]>(`${this.PRODUCT_ENDPOINT}/benefits/${productId}`);
  }

  /**
   * Get product coverages
   * GET /api/ih/product/coverages/{productId}
   */
  getProductCoverages(productId: string): Observable<ProductCoverage[]> {
    return this.get<ProductCoverage[]>(`${this.PRODUCT_ENDPOINT}/coverages/${productId}`);
  }

  /**
   * Get payment terms
   * GET /api/ih/product/payment-terms/{productId}
   */
  getPaymentTerms(productId: string): Observable<PaymentTerm[]> {
    return this.get<PaymentTerm[]>(`${this.PRODUCT_ENDPOINT}/payment-terms/${productId}`);
  }

  /**
   * Get recommended products for user
   * GET /api/ih/product/recommended/{phone}
   */
  getRecommendedProducts(phone: string): Observable<ProductDetail[]> {
    return this.get<ProductDetail[]>(`${this.PRODUCT_ENDPOINT}/recommended/${phone}`);
  }

  /**
   * Get plan thumbnails for carousel display
   * GET /api/ih/product/carousel/thumbnails
   */
  getCarouselThumbnails(): Observable<PlanThumbnail[]> {
    return this.get<PlanThumbnail[]>(`${this.PRODUCT_ENDPOINT}/carousel/thumbnails`);
  }

  /**
   * Get personalized plan thumbnails for user's policies
   * GET /api/ih/product/carousel/thumbnails/{phone}
   */
  getUserCarouselThumbnails(phone: string): Observable<PlanThumbnail[]> {
    return this.get<PlanThumbnail[]>(`${this.PRODUCT_ENDPOINT}/carousel/thumbnails/${phone}`);
  }

  /**
   * Get available riders for a plan
   * GET /api/ih/product/plan/{planId}/riders
   */
  getPlanRiders(planId: string): Observable<ProductRider[]> {
    return this.get<ProductRider[]>(`${this.PRODUCT_ENDPOINT}/plan/${planId}/riders`);
  }

  /**
   * Get plan details from I4U API (includes premiumTypeId)
   * GET /api/i4u/product/{productId}/{planId}
   * Returns: { plan, product, productOptions, paymentTerms, productCoverages }
   */
  getPlanDetailsFromI4U(productId: string, planId: string): Observable<any> {
    // Call I4U API endpoint directly - use base URL without /ih prefix
    const baseUrl = this.apiUrl.replace('/api/ih', '/api');
    return this.http.get(`${baseUrl}/i4u/product/${productId}/${planId}`);
  }

  findProductsPlanByProductId(name: any, sellableOnly = false) {
    const baseUrl = this.apiUrl.replace('/api/ih', '/api');
    const url = `${baseUrl}/i4u/product/plans?productId=${name}`;
    return this.http.get(sellableOnly ? `${url}&sellableOnly=true` : url);
  }

  findProductPlanById(name: any, subName: any) {
    const baseUrl = this.apiUrl.replace('/api/ih', '/api');
    return this.http.get(`${baseUrl}/i4u/product/${name}/${subName}`);
  }
}
